import {useExport, ExpertSystemEquipmentType} from '@domain/expert-system'
import {downloadFile} from '@domain/file-download'
import {PlantViewToggle} from '@hconnect/common/components'
import {PlantViewOption, PlantViewOptions} from '@hconnect/common/consts'
import {getButtonOutlineWhite} from '@hconnect/common/styles'
import {getSystemTimezone, setOrDeleteStringParam} from '@hconnect/common/utils'
import {
  CalendarDatePicker,
  CalendarDatePickerRange,
  ExpandCollapseButton,
  IconWithLabelButton,
  useIsMobile
} from '@hconnect/uikit'
import {buttonOnBlueSx} from '@hconnect/uikit/src/lib2'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {Box, FormControlLabel, Stack, Switch, Tooltip} from '@mui/material'
import moment from 'moment-timezone'
import {FC, useCallback, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {NavigateFunction, useNavigate, useSearchParams} from 'react-router'

import {FEATURE_NAMESPACE} from '../consts'
import {handleError} from '../hooks'
import {PAGE_PARAMS} from '../pages/ExpertSystemUsageRatePage'
import {getExpandCollapseButtonSx} from '../styles'

import {EquipmentTypeToggle} from './EquipmentTypeToggle'

type ExpertSystemUsageActionsProps = {
  allExpanded: boolean
  toggleExpandCollapseAll: () => void
  plantView: PlantViewOption
  setPlantView: (value: PlantViewOption) => void
  equipmentType: ExpertSystemEquipmentType
  setEquipmentType: (value: ExpertSystemEquipmentType) => void
  year: number
  setYear: (year: string) => void
  isEditMode: boolean
  setIsEditMode: (isEditMode: boolean) => void
  isMonthlyView: boolean
  setIsMonthlyView: (isMonthlyView: boolean) => void
  month: number
  // true when hosted on the legacy blue surface; false (default) on the Suite grey surface
  inverted?: boolean
}

export const ExpertSystemUsageActions: FC<ExpertSystemUsageActionsProps> = ({
  allExpanded,
  toggleExpandCollapseAll,
  setPlantView,
  plantView,
  equipmentType,
  setEquipmentType,
  year,
  setYear: setYear,
  isEditMode,
  setIsEditMode,
  isMonthlyView,
  setIsMonthlyView,
  month,
  inverted = false
}) => {
  const navigate: NavigateFunction = useNavigate()
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const isMobile = useIsMobile()
  const {refetch, isRefetching, error} = useExport(equipmentType, year, {enabled: false})
  const [, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (error) {
      handleError(error, navigate)
    }
  }, [error, navigate])

  const calendarType = isMonthlyView
    ? CalendarDatePickerRange.Monthly
    : CalendarDatePickerRange.Yearly

  const maxDateAllowed = moment().endOf(isMonthlyView ? 'month' : 'year')

  const selectedDate = useMemo(
    () =>
      isMonthlyView
        ? moment({year, month: month - 1}).toISOString()
        : moment({year}).add(1, 'months').toISOString(),
    [isMonthlyView, year, month]
  )

  const setMonthAndYear = useCallback(
    (month: string, year: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        setOrDeleteStringParam(newParams, PAGE_PARAMS.MONTH, month)
        setOrDeleteStringParam(newParams, PAGE_PARAMS.YEAR, year)
        return newParams
      })
    },
    [setSearchParams]
  )

  return (
    <Stack
      pl={0.5}
      direction={'row'}
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{justifyContent: 'space-between', width: '100%'}}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <>
          <label>{t('label.monthly')}</label>
          <FormControlLabel
            sx={{mr: 1}}
            control={
              <Switch
                checked={isMonthlyView}
                data-test-id="expert-system-usage-calendar-mode-toggle"
                onChange={() => setIsMonthlyView(!isMonthlyView)}
                color="primary"
              />
            }
            label={t('label.daily')}
            labelPlacement="end"
          />
        </>
        <Box maxWidth={250}>
          <CalendarDatePicker
            label={t(`label.${calendarType}`)}
            selectedDate={selectedDate}
            contrastingText={!inverted}
            timeZone={getSystemTimezone()}
            setSelectedDate={(date) => {
              const newValue = date ? moment(date) : moment()
              if (isMonthlyView) {
                return setMonthAndYear(
                  newValue.format('MM').toString(),
                  newValue.format('YYYY').toString()
                )
              }
              setYear(newValue.format('YYYY').toString())
            }}
            type={calendarType}
            maxDateAllowed={maxDateAllowed}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {!isMobile && (
          <FormControlLabel
            sx={{mr: 1}}
            control={
              <Switch
                checked={isEditMode}
                data-test-id="expert-system-usage-edit-mode-toggle"
                onChange={() => setIsEditMode(!isEditMode)}
                color="primary"
              />
            }
            label={t('label.editMode')}
            labelPlacement="end"
          />
        )}
        {!isMobile ? (
          <>
            {plantView === PlantViewOptions.REGIONAL_STRUCTURE && (
              <ExpandCollapseButton
                sx={getExpandCollapseButtonSx(inverted)}
                collapseLabel={t('button.collapseAll')}
                expandLabel={t('button.expandAll')}
                expanded={allExpanded}
                onClick={toggleExpandCollapseAll}
                data-test-id="expand-collapse-button"
              />
            )}
            <EquipmentTypeToggle
              value={equipmentType}
              onChange={setEquipmentType}
              inverted={inverted}
            />
            <PlantViewToggle
              value={plantView}
              t={t}
              onChange={setPlantView}
              isContrastMode={inverted}
            />
            <Tooltip
              title={isMonthlyView ? t('action.exportOnlyAvailableInYearlyMode') : ''}
              placement="top"
            >
              <span>
                <IconWithLabelButton
                  disabled={isRefetching || isMonthlyView}
                  onClick={async () => {
                    const response = await refetch()
                    if (response.data) {
                      downloadFile(response.data, `${year}_ExpertSystemUsage.xlsx`)
                    }
                  }}
                  sx={inverted ? buttonOnBlueSx : getButtonOutlineWhite}
                  data-test-id="expert-system-download"
                  startIcon={<FileDownloadIcon />}
                  label={t('action.export')}
                />
              </span>
            </Tooltip>
          </>
        ) : (
          <EquipmentTypeToggle
            value={equipmentType}
            onChange={setEquipmentType}
            inverted={inverted}
          />
        )}
      </Stack>
    </Stack>
  )
}
